import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import { IOrderFinances, IProductList, ORDER_STATUS, ORDER_STATUS_ID_CONFIRMED_FINANCE } from 'types/orders';
import HeaderCard from '../HeaderCard/HeaderCard';
import { OrderPaymentInformation } from 'views/orders/Components';
import CheckOrAddCommentsModal from 'views/orders/Components/CheckOrAddCommentsModal/CheckOrAddCommentsModal';
import ModalConfirmPayment from 'components/modalConfirmPayment';
import { submitComment } from 'views/LogisticServices/utils/general.utils';
import { ICommentsAndObservations } from 'types/global';
import {
  getOrderCommentsByOrderId,
  getOrderDetailedData,
  getOrderResponsibleUsersList,
  updateOrderStatus
} from 'services/orders';
import { getOrganizationData } from 'services/bussines';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { formatRoundedUpNumber } from 'utils/formatNumbers';
import { CURRENCIES } from 'types/bussines';
import { VOLATILE_CURRENCIES } from 'utils/logisticServices.utils';
import ButtonConfirmOrder from 'views/orders/OrderDetailInternalCard/Components/ButtonConfirmOrder/ButtonConfirmOrder';
import './FinanceCard.scss';

/**
 * Componente que muestra la información financiera de una orden
 * @param data - Datos de la orden
 * @param queryParams - Parámetros de búsqueda actuales
 * @param setQueryParams - Función para actualizar los parámetros de búsqueda
 * @param isPendingConfirmation - Indica si la orden está en estado pendiente de confirmación
 */
const FinanceCard = ({ data, queryParams, setQueryParams, isPendingConfirmation }: IFinanceCard) => {
  const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false);
  const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState<boolean>(false);
  const [confirmPaymentData, setConfirmPaymentData] = useState<IProductList>();
  const [loadingComments, setLoadingComments] = useState<boolean>(false);
  const [loadingConfirmation, setLoadingConfirmation] = useState<boolean>(false);
  const [loadingOpenModal, setLoadingOpenModal] = useState<boolean>(false);
  const [dataComments, setDataComments] = useState<ICommentsAndObservations[]>();
  const { orderId, order } = data || {};
  const { organizationId, PaymentType, orderPrice, committedPaymentDate } = order || {};
  const paymentType = PaymentType?.name || '';
  const shouldShowConfirmPaymentModal = showConfirmPaymentModal && !!confirmPaymentData;
  const shouldShowCommentsModal = showCommentsModal && !!organizationId;

  const { data: responsibleUsersList } = useSWR(
    showCommentsModal && orderId ? [`order-responsible-${orderId}`, orderId] : null,
    () => getOrderResponsibleUsersList(Number(orderId)),
    {
      suspense: false,
      revalidateOnFocus: false,
      onError: () => {
        displayErrorNotification();
      }
    }
  );
  const { data: orderComments } = useSWR(
    showCommentsModal && orderId ? [`order-comments-${orderId}`, orderId] : null,
    () => getOrderCommentsByOrderId(Number(orderId)),
    {
      suspense: false,
      revalidateOnFocus: false,
      onError: () => {
        displayErrorNotification();
      }
    }
  );
  const handleOpenOrCloseCommentsModal = () => setShowCommentsModal(!showCommentsModal);

  const { data: organizationInfo } = useSWR(
    showCommentsModal && organizationId ? [`organization-${organizationId}`, organizationId] : null,
    () => getOrganizationData(Number(organizationId)),
    {
      suspense: false,
      revalidateOnFocus: false,
      onError: () => {
        displayErrorNotification();
      }
    }
  );

  const handleCommentsSubmission = async (comment: ICommentsAndObservations) => {
    if (!orderId) return;
    setLoadingComments(true);
    try {
      const kamUserId = Number(organizationInfo?.assignedKam?.id);
      await submitComment({
        orderId,
        allowNewCommentsSubmission: !loadingComments,
        handleLoading: setLoadingComments,
        comments: comment?.comments,
        mentions: comment?.mentions || [],
        commentImage: comment?.commentImage,
        ...(kamUserId && { kamUserId: kamUserId })
      });
      const response = await getOrderDetailedData(orderId);
      setDataComments(response?.orderComments || []);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingComments(false);
    }
  };

  const handleCloseConfirmPaymentModal = () => {
    setShowConfirmPaymentModal(false);
    setConfirmPaymentData(undefined);
  };

  const handleConfirmOrder = async (orderData: IProductList) => {
    if (!orderId) return;
    setLoadingConfirmation(true);

    try {
      const { invoiceNumber, paymentLink, committedPaymentDate: paymentDate, invoice, proofOfPayment } = orderData;
      const formData = new FormData();

      const bodyForm = {
        invoiceNumber,
        paymentLink,
        committedPaymentDate: paymentDate
      };

      const bodyJson = JSON.stringify(bodyForm);
      formData.append('body', bodyJson);

      if (invoice) formData.append('invoice', invoice);
      if (proofOfPayment) formData.append('proofOfPayment', proofOfPayment);

      await updateOrderStatus(orderId, ORDER_STATUS_ID_CONFIRMED_FINANCE, formData);
      displaySuccessNotification({ customJSXMessage: <>La confirmación de la orden fue realizado con éxito</> });
      handleCloseConfirmPaymentModal();
      window.dispatchEvent(new Event('orderStatusChanged'));
    } catch (error) {
      displayErrorNotification({ customJSXMessage: <>Error al confirmar la orden. Por favor intenta de nuevo</> });
    } finally {
      setLoadingConfirmation(false);
    }
  };

  useEffect(() => {
    setDataComments(orderComments);
  }, [orderComments]);

  return (
    <div className="financeCard">
      <HeaderCard
        data={data}
        openModal={handleOpenOrCloseCommentsModal}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
      />
      <div className="financeInfo">
        <div className="containerValues">
          <div className="valueRow">
            <span className="valueLabel">Método de pago:</span>
            <span className="valueAmount">{paymentType}</span>
          </div>
          <div className="valueRow">
            <span className="valueLabel">Monto:</span>
            <span className="valueAmount">{`$${
              Object.values(CURRENCIES).some(currency => currency === orderPrice?.currency)
                ? formatRoundedUpNumber(Number(orderPrice?.totalValueUsd || 0), 2)
                : formatRoundedUpNumber(Number(orderPrice?.totalValue || 0), 2)
            } ${
              VOLATILE_CURRENCIES.some(currency => currency === orderPrice?.currency)
                ? CURRENCIES.USD
                : orderPrice?.currency
            }`}</span>
          </div>
          <div className="valueRow">
            <span className="valueLabel">Factura:</span>
            <span className="valueAmount">
              <OrderPaymentInformation orderData={data} invoiceShow={true} />
            </span>
          </div>
          <div className="valueRow">
            <span className="valueLabel">Pago:</span>
            <span className="valueAmount">
              <OrderPaymentInformation orderData={data} />
            </span>
          </div>
          {!!committedPaymentDate && (
            <div className="valueRow">
              <span className="valueLabel">Acuerdo de pago:</span>
              <span className="valueAmount">
                <OrderPaymentInformation orderData={data} isCommittedPaymentAgreement={true} />
              </span>
            </div>
          )}
        </div>
      </div>
      {isPendingConfirmation && (
        <div className="confirmOrderButtonContainer">
          <ButtonConfirmOrder
            orderId={orderId}
            orderStatusName={ORDER_STATUS.PENDING}
            loadingOpenConfirmPaymentModal={loadingOpenModal}
            setLoadingOpenConfirmPaymentModal={setLoadingOpenModal}
            setShowConfirmPaymentModal={setShowConfirmPaymentModal}
            setConfirmPaymentData={setConfirmPaymentData}
            customClassName="confirmOrderButtonFinanceCard"
          />
        </div>
      )}
      {shouldShowConfirmPaymentModal && (
        <ModalConfirmPayment
          updateData={confirmPaymentData}
          actionUpdate={handleConfirmOrder}
          getGeneralData={handleCloseConfirmPaymentModal}
          loadingUpdate={loadingConfirmation}
        />
      )}
      {shouldShowCommentsModal && (
        <CheckOrAddCommentsModal
          closeModal={handleOpenOrCloseCommentsModal}
          orderComments={dataComments}
          handleSubmitComments={handleCommentsSubmission}
          loading={loadingComments}
          responsibleUsers={responsibleUsersList}
          organizationId={organizationId}
        />
      )}
    </div>
  );
};

interface IFinanceCard {
  data: IOrderFinances;
  queryParams?: string;
  setQueryParams?: (queryParams: string) => void;
  isPendingConfirmation?: boolean;
}

export default React.memo(FinanceCard, (prevProps: IFinanceCard, nextProps: IFinanceCard) => {
  return (
    prevProps.data?.orderId === nextProps.data?.orderId &&
    prevProps.data?.order?.total === nextProps.data?.order?.total &&
    prevProps.isPendingConfirmation === nextProps.isPendingConfirmation &&
    JSON.stringify(prevProps.data?.orderComments) === JSON.stringify(nextProps.data?.orderComments)
  );
});
